import { useLayoutEffect, useRef, type ComponentProps } from "react";
import { reserveEmphasisWidth } from "@/lib/reserve-emphasis-width";
import {
  buttonClasses,
  type ButtonSize,
  type ButtonVariant,
} from "./button-classes";

// React component wrapper around the pure `buttonClasses()` seam in
// ./button-classes. Non-<button> consumers (e.g. <Link>) skip this file and
// import `buttonClasses` directly from `./button-classes`.

interface ButtonProps extends ComponentProps<"button"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  reserveEmphasis?: boolean;
}

export function Button({
  variant,
  size,
  className,
  type = "button",
  reserveEmphasis = false,
  children,
  ...props
}: ButtonProps) {
  const labelRef = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    const label = labelRef.current;
    if (!reserveEmphasis || !label) return;
    const measure = () => reserveEmphasisWidth(label);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(label);
    return () => observer.disconnect();
  }, [children, reserveEmphasis, variant]);
  return (
    <button
      type={type}
      className={buttonClasses({ variant, size, className })}
      {...props}
    >
      {reserveEmphasis ? <span ref={labelRef} className="smycles-emphasis-label">{children}</span> : children}
    </button>
  );
}
